import { useCallback, useEffect, useRef, useState } from 'react';
import { getMeetingPopupParentOrigin, isMeetingPopup } from '../services/meetingWindow/popupMode';
import {
  isMeetingWindowMessage,
  MEETING_WINDOW_MESSAGE_SOURCE,
} from '../services/meetingWindow/protocol';

export interface MeetingSwitchRequest {
  requestId: string;
  /** The room the user clicked in the host, e.g. from a Chat message. */
  meetingId: string;
}

export interface MeetingSwitchRequestState {
  /** The pending prompt, or `null` when there is nothing to ask. */
  request: MeetingSwitchRequest | null;
  /** Leave this meeting and let the opener move the window to the new room. */
  accept: () => void;
  /** Stay in this meeting; the opener drops the switch. */
  decline: () => void;
}

/**
 * Meeting-window side of the switch handshake.
 *
 * The opener asks here — rather than prompting in TeamHub — because this is the
 * window holding the meeting the user would be leaving, and because the opener
 * may have no Meet UI mounted at all when the link is clicked from Chat
 * (SM-1254). Only the opener can perform the switch, so this hook's whole job is
 * to relay the user's answer back.
 *
 * Inert outside a meeting popup: the standalone app and the embedded remote
 * never receive these messages.
 */
export const useMeetingSwitchRequest = (): MeetingSwitchRequestState => {
  const [request, setRequest] = useState<MeetingSwitchRequest | null>(null);
  const parentOriginRef = useRef<string | null>(getMeetingPopupParentOrigin());
  // Read by the answer callbacks so they stay stable across renders, mirroring
  // the snapshot ref in `usePopupHostAuth`.
  const requestRef = useRef<MeetingSwitchRequest | null>(request);
  requestRef.current = request;

  useEffect(() => {
    if (!isMeetingPopup()) {
      return;
    }
    const opener = window.opener as Window | null;
    const parentOrigin = parentOriginRef.current;
    if (!(opener && parentOrigin)) {
      return;
    }

    const onMessage = (event: MessageEvent): void => {
      if (event.origin !== parentOrigin || event.source !== opener) {
        return;
      }
      if (!isMeetingWindowMessage(event.data) || event.data.type !== 'switch-request') {
        return;
      }
      // The newest click wins: replace any prompt still on screen rather than
      // queueing, so the user is never asked about a room they moved on from.
      setRequest({ requestId: event.data.requestId, meetingId: event.data.meetingId });
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const answer = useCallback((accepted: boolean): void => {
    const current = requestRef.current;
    if (!current) {
      return;
    }
    setRequest(null);
    const opener = window.opener as Window | null;
    const parentOrigin = parentOriginRef.current;
    if (!(opener && parentOrigin)) {
      return;
    }
    opener.postMessage(
      {
        source: MEETING_WINDOW_MESSAGE_SOURCE,
        type: 'switch-response',
        requestId: current.requestId,
        accepted,
      },
      parentOrigin,
    );
  }, []);

  const accept = useCallback(() => answer(true), [answer]);
  const decline = useCallback(() => answer(false), [answer]);

  return { request, accept, decline };
};
